import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AllUserMeetingsResponse,
  AllUserSessionRecordingsResponse,
  DyteTokenResponse,
  LiveClass,
  LiveClassResponseDTO,
  Meeting,
  MeetingEvents,
  MeetingResponseDTO,
  ZoomMeeting,
  ZoomTokenResponse,
} from './models/live-class';
import { LiveClassResponse } from './models/live-class.dto';

import { environment } from '../../../../../environments/environment';
import {
  Invoice,
  PublicTenantPaymentConfig,
} from '../../../models/invoice.model';
import { Billing, Payment } from '../order/order.model';
import { PaymentType, Subscription } from '../subscription/subscription.model';
import { RecordingSessionDTO } from '../../../models/session-recording.model';
import { StudyMaterialResponseDTO } from '../../../models/study-material.model';
import { AnnouncementItem } from '../../../models/announcement.model';

export interface PastSession {
  meetingId: string;
  meetingName: string;
  sessionDate: Date;
}
@Injectable()
export class LiveClassService {
  private http = inject(HttpClient);

  private baseUrl = environment.host;

  create(liveClass: LiveClass) {
    return this.http.post<LiveClass>(
      this.baseUrl + '/api/v1/live-class',
      liveClass,
    );
  }

  getAll(page: number, limit = 9, status?: string, includeUnlisted?: boolean) {
    let url =
      this.baseUrl + '/api/v1/live-class?page=' + page + '&limit=' + limit;
    if (status) url += '&status=' + encodeURIComponent(status);
    if (includeUnlisted) url += '&includeUnlisted=true';
    return this.http.get<any>(url);
  }

  getAllForInstructor() {
    return this.http.get<LiveClassResponse>(
      this.baseUrl + '/api/v1/live-class/instructor',
    );
  }

  getById(id: string) {
    return this.http.get<LiveClassResponseDTO>(
      this.baseUrl + '/api/v1/live-class/' + id,
    );
  }

  update(liveClassId: string, liveClass: LiveClass) {
    return this.http.put<LiveClass>(
      this.baseUrl + '/api/v1/live-class/' + liveClassId,
      liveClass,
    );
  }

  requestForReview(id: string) {
    return this.http.put<LiveClass>(
      this.baseUrl + '/api/v1/live-class/' + id + '/submit',
      {},
    );
  }

  publish(id: string) {
    return this.http.put<LiveClass>(
      this.baseUrl + '/api/v1/live-class/' + id + '/publish',
      {},
    );
  }

  archiveLiveClass(id: string) {
    return this.http.put<LiveClass>(
      this.baseUrl + '/api/v1/live-class/' + id + '/archive',
      {},
    );
  }

  getMeeting(meetingId: string) {
    return this.http.get<Meeting>(
      this.baseUrl + '/api/v1/meeting/' + meetingId,
    );
  }

  getRealtimeKitMeeting(meetingId: string) {
    return this.http.get<Meeting>(
      this.baseUrl + '/api/v1/meeting/' + meetingId + '/rtk',
    );
  }

  getDyteMeeting(meetingId: string) {
    return this.getRealtimeKitMeeting(meetingId);
  }

  getRealtimeKitToken(meetingId: string) {
    return this.http.post<DyteTokenResponse>(
      this.baseUrl + '/api/v1/auth/rtk-token',
      {
        meetingId,
      },
    );
  }

  getDyteToken(dyteMeetingId: string) {
    return this.getRealtimeKitToken(dyteMeetingId);
  }

  // Fetch Meeting info for Meetings
  getZoomMeeting(meetingId: string) {
    return this.http.get<Meeting>(
      this.baseUrl + '/api/v1/meeting/' + meetingId + '/zoom',
    );
  }

  // Fetch Zoom Meeting info from Zoom API
  getZoomMeetingInfoFromAPI(shareLinkId: string) {
    return this.http.get<ZoomMeeting>(
      this.baseUrl + '/api/v1/meeting/' + shareLinkId + '/zoom-info',
    );
  }

  getZoomToken(zoomMeetingId: string) {
    return this.http.post<ZoomTokenResponse>(
      this.baseUrl + '/api/v1/auth/zoom-meet-token',
      {
        meetingNumber: zoomMeetingId,
      },
    );
  }

  getMeetingByLinkId(shareLinkId: string) {
    return this.http.get<Meeting>(
      this.baseUrl + '/api/v1/meeting/share/' + shareLinkId,
    );
  }

  subscribeToLiveClass(
    liveClassId: string,
    paymentType: PaymentType,
    targetEvent?: { meetingId: string; eventDate: Date },
  ) {
    return this.http.post<{
      unpaidInvoice: Invoice | null;
      subscription: Subscription;
    }>(this.baseUrl + '/api/v1/subscription?with-invoice=true', {
      liveClass: liveClassId,
      paymentType,
      targetEvent,
    });
  }

  processPayment(invoiceId: string, payment: Payment) {
    return this.http.put<Invoice>(
      this.baseUrl + '/api/v1/subscription/invoice/' + invoiceId + '/payment',
      payment,
    );
  }

  initiateInvoicePayherePayment(
    invoiceId: string,
    billingInformation: Billing,
    returnUrl: string,
    cancelUrl: string,
  ) {
    return this.http.post<Record<string, string>>(
      this.baseUrl + '/api/v1/invoice/' + invoiceId + '/payhere/initiate',
      { billingInformation, returnUrl, cancelUrl },
    );
  }

  fetchInvoice(invoiceId: string) {
    return this.http.get<Invoice>(
      this.baseUrl + '/api/v1/invoice/' + invoiceId,
    );
  }

  getInvoicePaymentConfig(invoiceId: string) {
    return this.http.get<PublicTenantPaymentConfig>(
      this.baseUrl + '/api/v1/invoice/' + invoiceId + '/payment-config',
    );
  }

  fetchAllMeetingSessions(
    fromDateString?: string,
    toDateString?: string,
    includeAll = true,
  ) {
    const params: any = {};
    if (fromDateString) params.from = fromDateString;
    if (toDateString) params.to = toDateString;
    if (includeAll) params.includeAll = 'true';

    return this.http.get<MeetingEvents[]>(
      this.baseUrl + '/api/v1/subscription/fetch-active-sessions',
      { params },
    );
  }

  // Subscription based meetings
  fetchAllMeetings() {
    return this.http.get<AllUserMeetingsResponse>(
      this.baseUrl + '/api/v1/subscription/fetch-all-meetings',
    );
  }

  // Subscription based recordings
  fetchAllRecordings() {
    return this.http.get<AllUserSessionRecordingsResponse>(
      this.baseUrl + '/api/v1/subscription/fetch-all-recordings',
    );
  }

  fetchAllSubscribedClasses() {
    return this.http.get<LiveClass[]>(
      this.baseUrl + '/api/v1/subscription/fetch-all-live-classes',
    );
  }

  // All Meetings (For Admins)
  getAllMeetings(page: number, limit: number, provider?: string) {
    let url = `${environment.host}/api/v1/meeting?page=${page}&limit=${limit}`;
    if (provider) url += `&provider=${encodeURIComponent(provider)}`;
    return this.http.get<MeetingResponseDTO>(url);
  }

  generateZoomMeeting(meetingLinkId: string) {
    return this.http.post<Meeting>(
      this.baseUrl + '/api/v1/meeting/' + meetingLinkId + '/zoom',
      {},
    );
  }

  generateRealtimeKitMeeting(meetingLinkId: string) {
    return this.http.post<Meeting>(
      this.baseUrl + '/api/v1/meeting/' + meetingLinkId + '/rtk',
      {},
    );
  }

  getAllRecordings(liveClassId: string) {
    return this.http.get<RecordingSessionDTO[]>(
      this.baseUrl + '/api/v1/live-class/' + liveClassId + '/recordings',
    );
  }

  getPastSessions(liveClassId: string) {
    return this.http.get<PastSession[]>(
      this.baseUrl + '/api/v1/live-class/' + liveClassId + '/past-sessions',
    );
  }

  deleteRecording(recordingId: string) {
    return this.http.delete(
      this.baseUrl + '/api/v1/recording-session/' + recordingId,
    );
  }

  getAllStudyMaterials(liveClassId: string) {
    return this.http.get<StudyMaterialResponseDTO[]>(
      `${this.baseUrl}/api/v1/study-materials?liveClassId=${liveClassId}`,
    );
  }

  switchMeetingProvider(shareLinkId: string, newProvider: string) {
    return this.http.post<MeetingResponseDTO>(
      `${environment.host}/api/v1/meeting/${shareLinkId}/switch-provider`,
      { provider: newProvider },
    );
  }

  updateMeetingDirectLink(shareLinkId: string, directJoinUrl?: string) {
    return this.http.patch<MeetingResponseDTO>(
      `${environment.host}/api/v1/meeting/${shareLinkId}/direct-link`,
      { directJoinUrl },
    );
  }

  getAnnouncements(liveClassId: string) {
    return this.http.get<{ success: boolean; data: AnnouncementItem[] }>(
      `${this.baseUrl}/api/v1/live-class/${liveClassId}/announcements`,
    );
  }

  createAnnouncement(
    liveClassId: string,
    payload: { title: string; message: string; pinned?: boolean },
  ) {
    return this.http.post<{ success: boolean; data: AnnouncementItem }>(
      `${this.baseUrl}/api/v1/live-class/${liveClassId}/announcements`,
      payload,
    );
  }

  deleteAnnouncement(liveClassId: string, announcementId: string) {
    return this.http.delete<{ success: boolean }>(
      `${this.baseUrl}/api/v1/live-class/${liveClassId}/announcements/${announcementId}`,
    );
  }
}
